import React, { useState } from "react"
import { LayersList } from "@deck.gl/core"
import { MapboxOverlay, MapboxOverlayProps } from "@deck.gl/mapbox"
import classNames from "classnames"
import Map, { MapRef, useControl } from "react-map-gl"
import { mapSnapshotDefaultViewport } from "./constants"
import { MapboxMarkerIcon } from "./layers/icons/MapboxMarker"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { ButtonSecondary } from "@nova/component-btn"
import { Icon } from "@nova/protobuf"
import { StyleSpecification } from "maplibre-gl"
import { useMapStylesJSON } from "./control/selectors"

type DeckGLOverlayProps = MapboxOverlayProps & {
    interleaved?: boolean
}

export function DeckGLOverlay({ ...overlayProps }: DeckGLOverlayProps) {
    const overlay = useControl<MapboxOverlay>(() => new MapboxOverlay(overlayProps))
    overlay.setProps(overlayProps)

    return null
}

type MapPreviewProps = {
    title?: string
    layers?: LayersList
    viewport?: {
        latitude?: number
        longitude?: number
        zoom?: number
    }
    icons?: Icon[]
    className?: string
    onClick?: () => Promise<void>
    hideButton?: boolean
}
export function MapPreview({
    layers,
    title,
    viewport,
    icons,
    className,
    onClick,
    hideButton,
}: MapPreviewProps) {
    const { isEditor } = useWorkspacePermissions()
    const mapStylesJSON = useMapStylesJSON()
    const [loading, setLoading] = useState(true)

    const snapshotViewport = viewport || { ...mapSnapshotDefaultViewport, zoom: 2 }
    return (
        <div className="relative w-full">
            <div
                className={classNames(
                    "relative flex justify-center items-center cursor-pointer h-[300px] opacity-0",
                    { "opacity-100 ": !loading },
                    className
                )}
                onClick={() => {
                    if (isEditor) onClick?.()
                }}
            >
                <Map
                    {...snapshotViewport}
                    mapStyle={mapStylesJSON as StyleSpecification}
                    dragPan={false}
                    scrollZoom={false}
                    dragRotate={false}
                    onLoad={() => setLoading(false)}
                    maplibreLogo={false}
                    attributionControl={false}
                >
                    <DeckGLOverlay layers={layers} />
                    {icons?.map((icon, idx) => (
                        <MapboxMarkerIcon key={idx} icon={icon} />
                    ))}
                </Map>
                {!hideButton && isEditor && (
                    <ButtonSecondary
                        title={title}
                        className="!absolute z-5 top-[0.438rem] left-[0.438rem]"
                    />
                )}
            </div>
            {loading && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <NovaSpinner width={60} height={60} />
                </div>
            )}
        </div>
    )
}

export function fitMapToBounds<T extends (readonly [number, number])[]>(map: MapRef, coords: T) {
    const lngs = coords.map((c) => c[0])
    const lats = coords.map((c) => c[1])
    try {
        map.fitBounds(
            [
                [Math.min(...lngs), Math.min(...lats)],
                [Math.max(...lngs), Math.max(...lats)],
            ],
            { padding: 60, animate: false, maxZoom: 16 }
        )
    } catch {}
}
